import { ChangeEvent, MouseEvent, useCallback, useMemo, useState } from "react";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridToolbar,
  gridClasses,
} from "@mui/x-data-grid";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import Navbar from "../../Components/Navbar";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  TextField,
} from "@mui/material";
import { Edit } from "@mui/icons-material";
import { useBranchDistrictContext } from "../../Context/BranchDistrictContext";
import { StyledButton } from "../../Components/ui/CustomButton";
import { IBranchDistrictForm } from "../../@types/branchDistrict";
import {
  createBranchDistrict,
  updateBranchDistrict,
} from "../../Services/branchDistrictService";

const branchDistrictDetails: IBranchDistrictForm = {
  branchDistrictId: 0,
  branchDivisionId: 0,
  branchRegionId: 0,
  managerEmployeeId: 0,
  managerEmployeeGuid: "",
  title: "",
  isActive: false,
};

function BranchDistrictPage(): JSX.Element {
  const branchDistrictList = useBranchDistrictContext();
  const [open, setOpen] = useState(false);
  const [formValues, setFormValues] = useState<IBranchDistrictForm>(
    branchDistrictDetails
  );
  const [selectedDivisions, setSelectedDivisions] = useState("");
  const [selectedRegion, setSelectedRegions] = useState("");
  const [selectedEmployees, setSelectedEmployees] = useState("");
  const [districtId, setDistrictId] = useState("");
  const [title, setTitle] = useState("");
  const [error, setErrorText] = useState("");
  const branchDistrict = branchDistrictList?.branchDistrict;
  const divisions = branchDistrictList?.divisions;
  const regions = branchDistrictList?.regions;
  const employees = branchDistrictList?.employees;

  const handleEdit = useCallback(
    (params: any) => {
      setOpen(true);
      setDistrictId(params.row.branchDistrictId);
      setSelectedRegions(params.row.branchRegionId);
      setSelectedEmployees(params.row.managerEmployeeGuid);
      setSelectedDivisions(params.row.branchDivisionId);
      setTitle(params.row.title);
      setFormValues({
        ...formValues,
        branchDistrictId: params.row.branchDistrictId,
        branchDivisionId: params.row.branchDivisionId,
        branchRegionId: params.row.branchRegionId,
        managerEmployeeId: params.row.managerEmployeeId,
        managerEmployeeGuid: params.row.managerEmployeeGuid,
        title: params.row.title,
        isActive: params.row.isActive,
      });
    },
    [formValues]
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "branchDistrictId",
        flex: 0.75,
        headerClassName: "header-color",
        renderHeader: () => <strong>{"District ID "}</strong>,
        renderCell: (params) => (
          <strong style={{ fontWeight: 1000 }}>{params.value}</strong>
        ),
      },
      {
        field: "branchDivision",
        flex: 1,
        headerClassName: "header-color",
        renderHeader: () => <strong>{"Division"}</strong>,
      },
      {
        field: "branchRegion",
        flex: 1,
        headerClassName: "header-color",
        renderHeader: () => <strong>{" Region"}</strong>,
      },
      {
        field: "branchRegionId",
        flex: 1,
        headerClassName: "header-color",
        headerName: "Region ID",
        hide: true,
      },
      {
        field: "branchDivisionId",
        flex: 1,
        headerClassName: "header-color",
        headerName: "Division ID",
        hide: true,
      },
      {
        field: "isActive",
        flex: 0.75,
        headerClassName: "header-color",
        renderHeader: () => <strong>{"Active"}</strong>,
      },
      {
        field: "managerEmployeeId",
        flex: 1,
        headerClassName: "header-color",
        renderHeader: () => <strong>{"Manager Employee Id"}</strong>,
        hide: true,
      },
      {
        field: "manager",
        flex: 1,
        headerClassName: "header-color",
        renderHeader: () => <strong>{"Manager Name"}</strong>,
      },
      {
        field: "title",
        flex: 1,
        headerClassName: "header-color",
        renderHeader: () => <strong>{"Title"}</strong>,
      },
      {
        field: "actions",
        headerClassName: "header-color",
        editable: false,
        disableExport: true,
        renderHeader: () => <strong>{"Actions"}</strong>,
        renderCell: (params: GridRenderCellParams) => (
          <Edit
            onClick={() => {
              handleEdit(params);
            }}
            sx={{ cursor: "pointer" }}
          />
        ),
      },
    ],
    [handleEdit]
  );

  const filteredRows = useMemo(
    () => branchDistrict?.filter((row) => row.isActive),
    [branchDistrict]
  );

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setSelectedDivisions("");
    setSelectedEmployees("");
    setSelectedRegions("");
    setDistrictId("");
    setTitle("");
    setDistrictId("");
    setOpen(false);
  };

  const handleDivision = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setSelectedDivisions(event.target.value);
    setFormValues({
      ...formValues,
      [name]: value,
    });
  };

  const handleRegion = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setSelectedRegions(event.target.value);
    setFormValues({
      ...formValues,
      [name]: value,
    });
  };

  const handleSubmit = useCallback(
    async (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      if (!selectedEmployees || !selectedRegion) {
        setErrorText("Please select a value");
      } else {
        setErrorText("");
        if (formValues.branchDistrictId === 0) {
          await createBranchDistrict(formValues);
          //reset all values to blank after service call
          setFormValues(branchDistrictDetails);
          handleClose();
        } else {
          await updateBranchDistrict(formValues);
          //reset all values to blank after service call
          setFormValues(branchDistrictDetails);
          handleClose();
        }
        await branchDistrictList?.load();
      }
    },
    [
      formValues,
      branchDistrictList,
      selectedDivisions,
      selectedEmployees,
      selectedRegion,
    ]
  );

  const handleDelete = useCallback(
    async (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      formValues.isActive = false;
      await updateBranchDistrict(formValues);
      //reset all values to blank after service call
      setFormValues(branchDistrictDetails);
      handleClose();
      await branchDistrictList?.load();
    },
    [
      formValues,
      branchDistrictList,
      selectedDivisions,
      selectedEmployees,
      selectedRegion,
    ]
  );

  const handleTitleChange = (e: any) => {
    const { name, value } = e.target;
    setTitle(e.target.value);
    setFormValues({
      ...formValues,
      [name]: value,
    });
  };
  const handleEmployee = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setSelectedEmployees(value);
    const selectedEmployee = employees?.find(
      (employee) => employee.employeeGuid === value
    );
    setFormValues({
      ...formValues,
      [name]: value,
      managerEmployeeId: selectedEmployee?.employeeId ?? 0,
    });
  };

  return (
    <>
      <Navbar />
      <div className="paper-content">
        <div className="no-padding">
          <h1 className="grid-page-title">District Table</h1>
        </div>
        <div
          style={{
            height: 700,
            width: "100%",
            boxShadow: "0 3px 10px rgb(0 0 0 / 0.2)",
          }}
        >
          <DataGrid
            components={{ Toolbar: GridToolbar }}
            sx={{
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "rgb(245,245,245)",
              },
              padding: "2%",

              "& .header-color": {
                color: "#004d84",
                backgroundColor: "rgb(245,245,245)",
              },
              [`& .${gridClasses.cell}`]: {
                py: 1,
              },
            }}
            componentsProps={{
              toolbar: {
                csvOptions: { disableToolbarButton: false },
                printOptions: { disableToolbarButton: true },
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 250 },
              },
            }}
            rows={filteredRows || []}
            getRowId={(row) => row.branchDistrictId}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10]}
            initialState={{
              sorting: {
                sortModel: [{ field: "branchDistrictId", sort: "asc" }],
              },
            }}
          />
        </div>
        <br />
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <StyledButton variant="contained" onClick={handleClickOpen}>
            Add New District {<AddBoxOutlinedIcon />}
          </StyledButton>
        </div>
      </div>
      <div>
        <Dialog open={open} onClose={handleClose}>
          <DialogTitle>District - Save and Edit</DialogTitle>
          <DialogContent>
            <DialogContentText>
              <Box
                component="form"
                sx={{
                  "& .MuiTextField-root": { m: 1, width: "25ch" },
                }}
                noValidate
                autoComplete="off"
              >
                <div>
                  <TextField
                    disabled={true}
                    value={districtId}
                    id="outlined-basic"
                    name="BranchDistrictId"
                    placeholder="Branch District Id"
                    label="Branch District Id"
                    variant="outlined"
                  />
                  <TextField
                    onChange={handleTitleChange}
                    value={title}
                    id="outlined-basic"
                    name="Title"
                    placeholder="Title"
                    label="Title"
                    variant="outlined"
                  />
                  <TextField
                    id="outlined-select-Division"
                    select
                    fullWidth
                    name="BranchDivisionId"
                    label="Select Division"
                    value={selectedDivisions}
                    onChange={handleDivision}
                  >
                    {divisions &&
                      divisions.map((divisions: any) => (
                        <MenuItem
                          key={divisions.title}
                          value={divisions.branchDivisionId}
                        >
                          {divisions.title}
                        </MenuItem>
                      ))}
                  </TextField>
                  <TextField
                    id="outlined-select-Region"
                    select
                    required
                    error={!selectedRegion}
                    helperText={!selectedRegion ? error : ""}
                    name="BranchRegionId"
                    label="Select Region"
                    value={selectedRegion}
                    onChange={handleRegion}
                  >
                    {regions &&
                      regions.map((regions: any) => (
                        <MenuItem
                          key={regions.branchRegionId}
                          value={regions.branchRegionId}
                        >
                          {regions.title}
                        </MenuItem>
                      ))}
                  </TextField>
                  <TextField
                    id="outlined-select-Employee"
                    select
                    required
                    name="managerEmployeeGuid"
                    label="Select Employee"
                    error={!selectedEmployees}
                    helperText={!selectedEmployees ? error : ""}
                    value={selectedEmployees}
                    onChange={handleEmployee}
                  >
                    {employees &&
                      employees.map((employees: any) => (
                        <MenuItem
                          key={employees.employeeGuid}
                          value={employees.employeeGuid}
                        >
                          {employees.name}
                        </MenuItem>
                      ))}
                  </TextField>
                </div>
              </Box>
            </DialogContentText>
          </DialogContent>
          <DialogActions style={{ display: "flex" }}>
            <StyledButton
              onClick={handleDelete}
              style={{ background: "#FF0000", marginRight: "auto" }}
            >
              Delete
            </StyledButton>
            <StyledButton onClick={handleSubmit}>Submit</StyledButton>
            <StyledButton onClick={handleClose}>Cancel</StyledButton>
          </DialogActions>
        </Dialog>
      </div>
    </>
  );
}

export default BranchDistrictPage;
